import {Component, ElementRef, Input, OnInit, Renderer2} from '@angular/core';
import {UserCardResponse} from "../interface/userCardResponse";
import {UserCardService} from "../service/user-card.service";
import {ActivatedRoute, Router} from "@angular/router";
import {saveAs} from 'file-saver';
import {UserService} from "../service/user.service";
import {UserCardContentService} from "../service/user-card-content.service";
import {UserCardContent} from "../interface/userCardContent";
import {map} from "rxjs";
import {ContentResponse} from "../interface/contentResponse";
import {ContactType} from "../interface/contactType";
import {ContentType} from "../interface/contentType";
import {ImageType} from "../enums/image-type-enum";
import {ContentService} from "../service/content.service";
import {Meta} from "@angular/platform-browser";
import {CardsHistoryService} from "../service/cards-history.service";
import {FavoriteCardsService} from "../service/favorite-cards.service";
import {MockupDataModel} from "../qr-copy-message/mockup-data.model";
import {SnackBarUtil} from "../qr-copy-message/snack-bar-util.service";
import {COPIED_MASSAGE} from "../subscription-payment/subscription-payment.constant";
import {InsightsService} from "../service/insights.service";
import {UserCardReviewService} from "../service/user-card-review.service";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: 'app-cards-by-qr',
  templateUrl: './cards-by-qr.component.html',
  styleUrls: ['./cards-by-qr.component.scss', './cards-by-qr-media.component.scss']
})
export class CardsByQrComponent implements OnInit {
  userCard: UserCardResponse | any;
  content: ContentResponse | any;
  @Input() username: string | any
  documentUserCardContentList: UserCardContent[] = [];
  otherUserCardContentList: UserCardContent[] = [];
  showError: boolean = false;
  saveError: boolean = false;
  protected readonly ImageType = ImageType;
  isContentViewGrid: boolean | undefined;
  clickedOnStar: boolean | any;
  companyLogoExists: boolean = false;
  cardRating: number = 0.0;

  constructor(
    private userCardService: UserCardService,
    private route: ActivatedRoute,
    private userService: UserService,
    private router: Router,
    private userCardContentService: UserCardContentService,
    private renderer: Renderer2,
    private elRef: ElementRef,
    private contentService: ContentService,
    private meta: Meta,
    private cardsHistoryService: CardsHistoryService,
    private favoriteCardsService: FavoriteCardsService,
    private snackBar: SnackBarUtil,
    private insightsService: InsightsService,
    private userCardReviewService: UserCardReviewService,
    private translateService: TranslateService
  ) {
  }

  ngOnInit(): void {
    this.route.params.subscribe({
      next: (params) => {
        const qrCode = params['qrCode'];
        if (qrCode) {
          this.initIfQrCodeExists(qrCode);
        } else {
          this.showError = true;
        }
      }
    });
  }

  updateMetaTags() {
    this.meta.updateTag({
      property: 'og:title',
      content: `${this.userCard.name} ${this.userCard.surname} - ${this.userCard.specialization} | Scan Me`
    });
    this.meta.updateTag({
      property: 'og:description',
      content: `Here is my Digital Business Card from Popl, the #1 Digital Business Card Platform`
    });
    this.meta.updateTag({
      property: 'og:image',
      content: `${this.userCard.profilePic ? this.getUserCardImage(this.userCard.id, ImageType.PROFILE_PIC) : '/assets/svg/defaultUserIcon.svg'}`
    });
  }

  handleLinkClick(userCardContent: UserCardContent): void {
    const link = userCardContent.placeholder;
    this.insightsService.trackLinkTap(userCardContent.id).subscribe({
      next: () => {}
    });
    if (this.isValidUrl(link) || userCardContent.content.contentType === ContentType.CONTACT) {
      switch (userCardContent.content.contactType) {
        case ContactType.PHONE_NUMBER:
          this.openPhoneNumber(link);
          break;
        case ContactType.MESSAGE:
          this.openSMS(link);
          break;
        case ContactType.EMAIL:
          this.openEmail(link);
          break;
        case ContactType.WHATS_APP:
          this.openWhatsApp(link);
          break;
        case ContactType.VIBER:
          this.openViber(link);
          break;
        default:
          window.innerWidth > 576 ? window.open(link, '_blank') : window.location.href = link;
          break;
      }
    } else if (userCardContent.content.contentType === ContentType.PAYMENT
      && userCardContent.content.name.toLowerCase() === 'telcell') {
      this.copyToClipboardAndRedirect(userCardContent.placeholder);
    } else {
      this.router.navigate(['/error-page']).catch();
    }
  }

  copyToClipboardAndRedirect(value: string): void {
    navigator.clipboard.writeText(value).then(() => {
      this.showSnackbar(MockupDataModel.success(this.translateService.instant(COPIED_MASSAGE)), SnackBarUtil.CLASSES.success);
      window.open('https://qrtelcell.onelink.me/1hAx/sg625', '_blank');
    }).catch(err => {
      console.error('Failed to copy text: ', err);
    });
  }

  private openPhoneNumber(link: string): void {
    window.open('tel:' + link, '_blank');
  }

  private openSMS(link: string): void {
    window.open('sms:' + link, '_blank');
  }

  private openEmail(link: string): void {
    window.innerWidth > 576 ? window.open('mailto:' + link, '_blank') : window.location.href = 'mailto:' + link;
  }

  private openWhatsApp(link: string): void {
    const whatsappUrl = window.innerWidth > 576 ? 'https://web.whatsapp.com/send?phone=' + link : 'https://api.whatsapp.com/send?phone=' + link;
    window.innerWidth > 576 ? window.open(whatsappUrl, '_blank') : window.location.href = whatsappUrl;
  }

  private openViber(link: string): void {
    let cleanedLink = link;
    if (link.startsWith('+')) {
      cleanedLink = link.substring(1);
    }
    const viberUrl = 'viber://chat?number=%2B' + cleanedLink;
    window.innerWidth > 576 ? window.open(viberUrl, '_blank') : window.location.href = viberUrl;
  }

  getContentIcon(image: string) {
    return this.contentService.getImage(image)
  }

  onCreateProfile() {
    if (!this.userService.token) {
      this.router.navigate(['sign-up']).catch();
    } else {
      this.router.navigate(['cards']).catch();
    }
  }

  saveContact() {
    this.userCardService.saveContactAsVCard(this.userCard.id).subscribe({
      next: response => {
        const blob = new Blob([response], {type: 'text/vcard'});
        const fileName = this.userCard.name + " " + this.userCard.surname + ".vcf";
        saveAs(blob, fileName);
      },
      error: () => {
        this.saveError = true;
      }
    });
  }

  getUserCardImage(id: bigint, type: ImageType) {
    return this.userCardService.getCardImage(id, type);
  }

  isValidUrl(url: string): boolean {
    const urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].\S*$/i;
    return urlPattern.test(url);
  }

  changeFavouriteStatus(id: bigint, cardId: string, event: any) {
    event.stopPropagation();
    if (this.clickedOnStar) {
      this.favoriteCardsService.deleteFavoriteCardByCurrentUserIdAndCardId(id).subscribe({
        next: () => {
          this.clickedOnStar = false;
        },
        error: () => {
          this.router.navigate(['/login']).catch();
        }
      });
    } else {
      this.favoriteCardsService.saveFavoriteCardByCardId(id).subscribe({
        next: () => {
          this.clickedOnStar = true;
        },
        error: () => {
          this.router.navigate(['/login'], {state: {favoriteCardId: id, favoriteCardCardId: cardId}}).catch();
        }
      });
    }
  }

  handleDownload(cardContent: any) {
    const placeholder = cardContent.placeholder;
    if (!placeholder?.toLowerCase().endsWith('.pdf')) {
      this.openDocumentLink(placeholder);
      return;
    }
    this.userCardContentService.getDocument(placeholder).subscribe({
      next: (document: Blob) => {
        const docName = cardContent.linkTitle ? cardContent.linkTitle : cardContent.content.name;
        const extension = this.extractFileExtension(placeholder);
        saveAs(document, `${docName}.${extension}`);
      },
      error: (error) => {
        console.error('Document download failed', error);
      }
    });
  }

  private extractFileExtension(fileName: string): string {
    const dotIndex = fileName.lastIndexOf('.');
    if (dotIndex === -1 || dotIndex === fileName.length - 1) {
      return 'pdf';
    }
    return fileName.substring(dotIndex + 1);
  }

  private openDocumentLink(placeholder: string): void {
    if (!placeholder) {
      return;
    }
    const hasProtocol = /^https?:\/\//i.test(placeholder);
    const link = hasProtocol ? placeholder : `https://${placeholder}`;
    window.open(link, '_blank');
  }

  navigateToPayment(): void {
    this.router.navigate([`/${this.userCard.cardId}/payment`]).catch();
  }

  navigateToMenu(): void {
    if (!this.userCard?.menuId) {
      return;
    }
    this.router.navigate([`/menu/view/${this.userCard.menuId}`], {
      state: {qrCode: this.userCard.cardId}
    }).catch();
  }

  navigateToRatingPage(): void {
    this.router.navigate([`/${this.userCard.cardId}/rating`]).catch();
  }

  private initIfQrCodeExists(qrCode: string) {
    this.userCardService.getCardByQRCode(qrCode).subscribe({
      next: (userCardResponse) => {
        this.userCard = userCardResponse;
        this.isContentViewGrid = userCardResponse.contentViewGrid;
        const cardId = userCardResponse.id;

        this.checkIsCardSubscriptionExpired(userCardResponse)
        this.saveCardInHistory(cardId);
        this.initCompanyLogo(cardId);
        this.initClickedOnStarIfFavorite(cardId);
        this.updateMetaTags();
        this.initContent(cardId);
        this.initCardRating(cardId);
      },
      error: (err) => {
        if (err.status === 404) {
          this.router.navigate(['/error-page']);
        } else {
          this.showError = true;
        }
      }
    });
  }

  private initCardRating(cardId: bigint) {
    this.userCardReviewService.getAverageRatingByCardId(cardId).subscribe({
      next: (averageRating: number) => {
        this.cardRating = averageRating;
      }
    })
  }

  private checkIsCardSubscriptionExpired(userCard: UserCardResponse) {
    if (userCard.subscriptionExpired) {
      this.showError = true;
    }
  }

  private initContent(id: bigint) {
    this.userCardContentService.getByUserCardId(id)
      .pipe(
        map((userCardContentResponse: UserCardContent[]) => {
          const documentContents = userCardContentResponse.filter((userCardContent: UserCardContent) => userCardContent.content.contentType === ContentType.DOCUMENT && userCardContent.active);
          const nonDocumentContents = userCardContentResponse.filter((userCardContent: UserCardContent) => userCardContent.content.contentType !== ContentType.DOCUMENT && userCardContent.active);
          return {documentContents, nonDocumentContents};
        })
      )
      .subscribe({
        next: ({documentContents, nonDocumentContents}) => {
          this.documentUserCardContentList = documentContents;
          this.otherUserCardContentList = nonDocumentContents;
        }
      });
  }

  private initCompanyLogo(id: bigint) {
    const imageUrl = this.getUserCardImage(id, ImageType.COMPANY_LOGO);
    this.userCardService.checkImageExists(imageUrl).subscribe({
      next: (exists) => {
        this.companyLogoExists = exists;
      }
    });
  }

  private initClickedOnStarIfFavorite(id: bigint) {
    this.favoriteCardsService.getFavorites(Array.of(id)).subscribe({
      next: (favoritesMap: { [key: string]: boolean; }) => {
        const key = id.toString();
        this.clickedOnStar = favoritesMap[key] || false;
      },
      error: () => {
        this.clickedOnStar = false;
      }
    })
  }

  private saveCardInHistory(id: bigint) {
    this.cardsHistoryService.saveCardsHistory(id).subscribe({
      error: () => {
        this.showError = true;
      }
    })
  }

  private showSnackbar(data: MockupDataModel, panelClassNames: string[]): void {
    this.snackBar.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClassNames,
      data: data,
    });
  }
}
